import { styled } from "styled-components";

import { Image, MarkdownArea, MasterDetailContentWrapper, UIActionButton, UIButtonsRow } from "common/components";
import { breakpoints } from "common/styles";
import { hardNavigate } from "common/utilities/hardNavigate";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { shareLinkAction } from "domains/share/actions";
import type { Initiative } from "entities/initiatives/types";
import { getCanAccessDiscoverForumInitiatives } from "entities/permissions/selectors";

const StyledTitle = styled.h2`
  font-size: 2rem;
  margin: 5px 0;
`;

const StyledContent = styled.div`
  height: 100%;
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledButtons = styled(UIButtonsRow)`
  margin: 1rem 0;
`;

const StyledImage = styled(Image)`
  height: 200px;
  border-radius: 4px;

  @media ${breakpoints.medium} {
    height: 250px;
  }

  @media ${breakpoints.extraLarge} {
    height: 350px;
  }
`;

type InitiativeDetailProps = { initiative?: Initiative; shareLink: string };

export const InitiativeDetail = ({ initiative, shareLink }: InitiativeDetailProps) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const canViewToplinkInitiative = useSelector(getCanAccessDiscoverForumInitiatives) && initiative?.url;

  if (!initiative) {
    return null;
  }

  const onViewEventClick = () => {
    if (initiative.url) hardNavigate(initiative.url);
  };

  const onShareClick = () =>
    dispatch(
      shareLinkAction({
        id: initiative.id,
        name: initiative.name,
        link: `${import.meta.env.VITE_WEBSITE_URL}${shareLink}`,
        type: "Project",
      }),
    );

  return (
    <>
      <MasterDetailContentWrapper>
        <StyledImage src={initiative.images?.medium} alt={initiative.name} />
        <StyledContent>
          <StyledTitle>{initiative.name}</StyledTitle>
          <MarkdownArea>{initiative.description_markdown}</MarkdownArea>
          <StyledButtons>
            {canViewToplinkInitiative && (
              <UIActionButton
                type="open"
                onClick={onViewEventClick}
                label={translate("app.discover.topic.tabs.initiatives.view_initiative")}
              />
            )}
            <UIActionButton type="share" onClick={onShareClick} />
          </StyledButtons>
        </StyledContent>
      </MasterDetailContentWrapper>
    </>
  );
};
